import { FormControl, FormGroup } from '@angular/forms';
import { StrictBase } from '@wilson/base';

// Enums

export enum CompanyType {
  RailFreightUndertaking = 'rail_freight_undertaking',
  RailPassengerUndertaking = 'rail_passenger_undertaking',
  StaffServiceProvider = 'staff_service_provider',
  StaffLeasingCompany = 'staff_leasing_company',
}

export enum EmployeeCountOption {
  Employees1To10 = '1-10 employees',
  Employees11To50 = '11-50 employees',
  Employees51To200 = '51-200 employees',
  Employees201To500 = '201-500 employees',
  Employees501To1000 = '501-1,000 employees',
  Employees1001To5000 = '1,001-5,000 employees',
}
export const EMPLOYEE_COUNT_TRANSLATION_KEYS: Record<
  EmployeeCountOption,
  string
> = {
  [EmployeeCountOption.Employees1To10]: 'employees_1_to_10',
  [EmployeeCountOption.Employees11To50]: 'employees_11_to_50',
  [EmployeeCountOption.Employees51To200]: 'employees_51_to_200',
  [EmployeeCountOption.Employees201To500]: 'employees_201_to_500',
  [EmployeeCountOption.Employees501To1000]: 'employees_501_to_1000',
  [EmployeeCountOption.Employees1001To5000]: 'employees_1001_to_5000',
} as const;

export enum LanguageOption {
  German = 'german',
  English = 'english',
  French = 'french',
  Italian = 'italian',
  Dutch = 'dutch',
  Czech = 'czech',
  Polish = 'polish',
  Danish = 'danish',
}

export enum ServiceType {
  StaffLeasingShortTerm = 'staff_leasing_short_term',
  StaffLeasingLongTerm = 'staff_leasing_long_term',
  StaffingServices = 'staffing_services',
}

export enum RoleType {
  LocomotiveDriver = 'locomotive_driver',
  WagonInspector = 'wagon_inspector',
  TrainPersonnel = 'train_personnel',
  Shunters = 'shunters',
  SafetyStaff = 'safety_staff',
  SupervisionStaff = 'supervision_staff',
  Other = 'other',
}

export enum OperatingCountry {
  Germany = 'germany',
  Austria = 'austria',
  Switzerland = 'switzerland',
  France = 'france',
  CzechRepublic = 'czech_republic',
  Netherlands = 'netherlands',
  Poland = 'poland',
  Denmark = 'denmark',
  Belgium = 'belgium',
  Luxembourg = 'luxembourg',
  UnitedKingdom = 'united_kingdom',
}

export enum ShareCompanyProfileFormKeys {
  CompanyInfo = 'companyInfo',
  ContactDetails = 'contactDetails',
  Supply = 'supply',
  Demand = 'demand',
}

export enum CompanyInfoFormKeys {
  CompanyName = 'companyName',
  DisplayCompanyName = 'displayCompanyName',
  CompanyType = 'companyType',
  CompanyWebsite = 'companyWebsite',
  CompanyLogo = 'companyLogo',
  FurtherLocations = 'furtherLocations',
  FoundedYear = 'foundedYear',
  NumberOfEmployees = 'numberOfEmployees',
  CompanyClaim = 'companyClaim',
  CompanyDescription = 'companyDescription',
  LanguagesSupported = 'languagesSupported',
  CompanyTeaser = 'companyTeaser',
  CompanyAddress = 'companyAddress',
  Socials = 'socials',
}

export enum CompanyAddressFormKeys {
  Street = 'street',
  City = 'city',
  ZipCode = 'zipCode',
  State = 'state',
  Country = 'country',
}

export enum SocialsFormKeys {
  LinkedIn = 'linkedin',
}

export enum ContactDetailsFormKeys {
  FirstName = 'firstName',
  Surname = 'surname',
  Email = 'email',
  Phone = 'phone',
  Position = 'position',
}

export enum SupplyFormKeys {
  ServicesOffered = 'servicesOffered',
  RolesOffered = 'rolesOffered',
  ImportantQualificationsOffered = 'importantQualificationsOffered',
  OfferingText = 'offeringText',
  InterestedInText = 'interestedInText',
  OperatingCountries = 'operatingCountries',
  ReferenceCustomers = 'referenceCustomers',
}

export enum DemandFormKeys {
  ShareServicesInterestedIn = 'shareServicesInterestedIn',
  RolesInterestedIn = 'rolesInterestedIn',
  ImportantQualificationsInterestedIn = 'importantQualificationsInterestedIn',
  DemandText = 'demandText',
}

// Interfaces
export interface CompanyAddress {
  street: string;
  city: string;
  zipCode: string;
  state: string;
  country: string;
}

export interface ContactDetails {
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  position?: string;
}

export interface Supply {
  sharedServicesOffered: ServiceType[];
  rolesOffered: RoleType[];
  importantQualificationsOffered?: string;
  offeringText?: string;
  interestedInText?: string;
  operatingCountries?: OperatingCountry[];
  referenceCustomers?: string[];
}

export interface Demand {
  shareServicesInterestedIn: ServiceType[];
  rolesInterestedIn: RoleType[];
  importantQualificationsInterestedIn?: string;
  demandText?: string;
}

export interface Socials {
  linkedin?: string;
}

export interface ShareCompanyProfile {
  companyName: string | null;
  displayCompanyName: string | null;
  addressStreet: string;
  addressCity: string;
  addressZipCode: string;
  addressState: string;
  addressCountry: string;
  furtherLocations: string[];
  companyType: CompanyType;
  foundedYear: number | null;
  numberOfEmployees: EmployeeCountOption | null;
  companyWebsite: string;
  socialUrls: Record<string, string> | null;
  companyClaim: string | null;
  companyDescription: string | null;
  supportedLanguages: LanguageOption[];
  companyTeaser: string | null;
  companyLogo: string;
  contactFirstName: string;
  contactSurname: string;
  contactEmail: string;
  contactPhone: string;
  contactPosition: string | null;
  servicesOffered: ServiceType[];
  rolesOffered: RoleType[];
  importantQualificationsOffered: string | null;
  offeringText: string | null;
  operatingCountries: OperatingCountry[];
  referenceCustomers: string | null;
  servicesDemanded: ServiceType[];
  rolesDemanded: RoleType[];
  importantQualificationsDemanded: string | null;
  demandText: string | null;
  interestedInText: string | null;
}

export interface ShareCompanyProfilePostDto {
  companyName: string;
  displayCompanyName: string;
  companyAddress: CompanyAddress;
  companyType: CompanyType;
  companyWebsite: string;
  contactDetails: ContactDetails;
  supply: Supply;
  demand: Demand;
  furtherLocations: string[];
  foundedYear: number;
  numberOfEmployees: EmployeeCountOption;
  socials: Socials;
  companyClaim: string;
  companyDescription: string;
  languagesSupported: LanguageOption[];
}

export interface ShareCompanyProfileResponseDto
  extends ShareCompanyProfile,
    StrictBase {
  organizationalUnitId: string;
}

export interface ShareCompanyProfileFormValue {
  companyInfo: CompanyInfoFormValue;
  contactDetails: ContactDetailsFormValue;
  supply: SupplyFormValue;
  demand: DemandFormValue;
}

export interface CompanyInfoFormValue {
  companyName: string | null;
  displayCompanyName: string | null;
  companyType: CompanyType | null;
  companyWebsite: string | null;
  companyLogo: string | null;
  furtherLocations: string[] | null;
  foundedYear: Date | null;
  numberOfEmployees: EmployeeCountOption | null;
  companyClaim: string | null;
  companyDescription: string | null;
  languagesSupported: LanguageOption[] | null;
  companyTeaser: string | null;
  companyAddress: CompanyAddressFormValue;
  socials: SocialsFormValue;
}

export interface CompanyAddressFormValue {
  street: string | null;
  city: string | null;
  zipCode: string | null;
  state: string | null;
  country: string | null;
}

export interface SocialsFormValue {
  linkedin: string | null;
}

export interface ContactDetailsFormValue {
  firstName: string | null;
  surname: string | null;
  email: string | null;
  phone: string | null;
  position: string | null;
}

export interface SupplyFormValue {
  servicesOffered: ServiceType[] | null;
  rolesOffered: RoleType[] | null;
  importantQualificationsOffered: string | null;
  offeringText: string | null;
  interestedInText: string | null;
  operatingCountries: OperatingCountry[] | null;
  referenceCustomers: string[] | null;
}

export interface DemandFormValue {
  shareServicesInterestedIn: ServiceType[] | null;
  rolesInterestedIn: RoleType[] | null;
  importantQualificationsInterestedIn: string | null;
  demandText: string | null;
}

export interface ShareCompanyProfileFormControls {
  companyInfo: FormGroup<CompanyInfoFormControls>;
  contactDetails: FormGroup<ContactDetailsFormControls>;
  supply: FormGroup<SupplyFormControls>;
  demand: FormGroup<DemandFormControls>;
}

export interface CompanyInfoFormControls {
  companyName: FormControl<string | null>;
  displayCompanyName: FormControl<string | null>;
  companyType: FormControl<CompanyType | null>;
  companyWebsite: FormControl<string | null>;
  companyLogo: FormControl<string | null>;
  furtherLocations: FormControl<string[] | null>;
  foundedYear: FormControl<Date | null>;
  numberOfEmployees: FormControl<EmployeeCountOption | null>;
  companyClaim: FormControl<string | null>;
  companyDescription: FormControl<string | null>;
  languagesSupported: FormControl<LanguageOption[] | null>;
  companyTeaser: FormControl<string | null>;
  companyAddress: FormGroup<CompanyAddressFormControls>;
  socials: FormGroup<SocialsFormControls>;
}

export type CompanyAddressFormControls = {
  street: FormControl<string | null>;
  city: FormControl<string | null>;
  zipCode: FormControl<string | null>;
  state: FormControl<string | null>;
  country: FormControl<string | null>;
};

export type ContactDetailsFormControls = {
  firstName: FormControl<string | null>;
  surname: FormControl<string | null>;
  email: FormControl<string | null>;
  phone: FormControl<string | null>;
  position: FormControl<string | null>;
};

export type SupplyFormControls = {
  servicesOffered: FormControl<ServiceType[] | null>;
  rolesOffered: FormControl<RoleType[] | null>;
  importantQualificationsOffered: FormControl<string | null>;
  offeringText: FormControl<string | null>;
  interestedInText: FormControl<string | null>;
  operatingCountries: FormControl<OperatingCountry[] | null>;
  referenceCustomers: FormControl<string[] | null>;
};

export type DemandFormControls = {
  shareServicesInterestedIn: FormControl<ServiceType[] | null>;
  rolesInterestedIn: FormControl<RoleType[] | null>;
  importantQualificationsInterestedIn: FormControl<string | null>;
  demandText: FormControl<string | null>;
};

export type SocialsFormControls = {
  linkedin: FormControl<string | null>;
};
